import React, { useEffect, useState } from 'react'
import 'leaflet/dist/leaflet.css'

import {
  Entity,
  Event,
  EventMember,
  EventResponse,
  EventResult,
  EventResultObject,
  FetchResponse,
  Game,
  Member,
  MemberResponse,
  Message,
  Method,
  User,
  UserRole,
} from '../../../../shared/types'
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { getEventResults, getEvents, getMembers } from '../../../../store/data/data.selectors'
import CheckBoxComponent from '../../../../shared/components/CheckBoxComponent'
import { makeStyles } from '@mui/styles'
import getSortedResults from '../../../../util/getSortedResults'
import ButtonComponent from '../../../../shared/components/ButtonComponent'
import NewEventResults from './NewEventResults'
import DropDownComponent, { DropDownElement } from '../../../../shared/components/DropDownComponent'
import fetchData from '../../../../fetch/fetchData'
import { getApiVersion } from '../../../../shared/config/getApiVersion'
import Env from '../../../../shared/config/envConfig'
import * as dataActions from '../../../../store/data/data.actions'
import * as settingsActions from '../../../../store/settings/settings.actions'
import SaveIcon from '@mui/icons-material/Save'
import { getUser } from '../../../../store/settings/settings.selectors'
import getDefaultCompleteResult from '../../../../util/getDefaultCompleteResult'
import { addCsrfHeaders } from '../../../../shared/config/csrf'

interface EventResultsProps {
  eventMember?: EventMember[]
  eventId: string
}

const useStyles = makeStyles((theme: Theme) => ({
  playerResult: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tableHeader: {
    textAlign: 'left',
    fontWeight: 'bold',
    verticalAlign: 'bottom',
  },
  dealer: {
    backgroundColor: 'lightgrey',
  },
  stickyFooter: {
    position: 'sticky',
    bottom: 0,
  },
  footer: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  footerTitle: {
    fontWeight: 'bold',
    textAlign: 'right',
  },
  baseBox: {
    position: 'relative',
  },
}))

export interface PlayerTables {
  [key: number]: string[]
}

export interface TablePlayers {
  [key: string]: {
    session1: number
    session2: number
  }
}

export enum SortingOption {
  round1 = 'round1',
  round2 = 'round2',
  complete = 'complete',
}

// TODO stifte am ende der Zeile um evt die Berechnungen zu überschreiben
const EventResults: React.FC<EventResultsProps> = (props) => {
  const classes = useStyles()
  const basePath = Env.restBase
  const fetchHeaders = Env.fetchHeaders
  addCsrfHeaders(fetchHeaders)

  const dispatch = useDispatch()

  const restPath = Env.restBase
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')

  const user: User | undefined = useSelector(getUser)
  const members: MemberResponse | undefined = useSelector(getMembers)
  const events: EventResponse | undefined = useSelector(getEvents)
  const theme: Theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const storedEventResults: EventResultObject = useSelector(getEventResults)

  const [showResultDialog, setShowResultDialog] = React.useState<boolean>(false)
  const [editResultDialog, setEditResultDialog] = React.useState<boolean>(false)
  const [openNewStartDialog, setOpenNewStartDialog] = React.useState<boolean>(window.localStorage.getItem('openNewStartDialog') === 'true')
  const [saveAllowed, setSaveAllowed] = React.useState<boolean>(false)
  const [startAllowed, setStartAllowed] = React.useState<boolean>(false)
  const [eventStarted, setEventStartet] = React.useState<boolean>(false)
  const [showTablePlayerDialog, setShowTablePlayerDialog] = React.useState<boolean>(false)

  const [playerArray, setPlayerArray] = React.useState<string[]>([])
  const [playerOptions, setPlayerOptions] = React.useState<DropDownElement[]>([])
  const [newGamerId, setNewGamerId] = React.useState<string | undefined>()

  const [event, setEvent] = React.useState<Event | undefined>()
  const [eventResults, setEventResults] = React.useState<EventResult[]>([])

  const [sortingOption, setSortingOption] = React.useState<SortingOption>(SortingOption.complete)

  const [player1, setPlayer1] = React.useState<string | undefined>()
  const [player2, setPlayer2] = React.useState<string | undefined>()
  const [player3, setPlayer3] = React.useState<string | undefined>()
  const [player4, setPlayer4] = React.useState<string | undefined>()
  const [session, setSession] = React.useState<number | undefined>(undefined)
  const [table, setTable] = React.useState<number | undefined>(undefined)
  const [rounds, setRounds] = React.useState<number[]>([])
  const [selectedEventResult, setSelectedEventResult] = React.useState<EventResult>()

  const setEventMember = (data: FetchResponse) => {
    dispatch(dataActions.setEventMember({ count: data.count, results: data.results }))
  }
  const [message, setMessage] = useState<Message>()

  useEffect(() => {
    const results = storedEventResults[props.eventId] || []

    setEventResults(results)
    if (table && session) {
      const result = results.find((elem) => elem.table === table && elem.session === session)
      setSelectedEventResult(result)

      const games: number[] = []
      result && result.games && Object.keys(result.games).map(round => {
        games.push(parseInt(round, 10))
      })
      setRounds(games)
    }
  }, [storedEventResults, table, session])

  useEffect(() => {
    message && dispatch(settingsActions.setMessage(message))
  }, [message])

  useEffect(() => {
    const actualEvent = events?.results.find(event => event.id === props.eventId)
    actualEvent && setEvent(actualEvent)
  }, [events])

  const getPlayerName = (originId?: string) => {
    const member = originId && members?.results.find(member => member.originId === originId)
    return member ? `${member.firstname} ${member.lastname}` : null
  }

  const getSessionTables = () => {
    const tables: DropDownElement[] = []
    const gamerCount = props.eventMember?.filter(elem => elem.gamer).length
    const tableCount = gamerCount && Math.floor(gamerCount / 3)
    if (tableCount) {
      for (let i = 1; i <= tableCount; i++) {
        tables.push({
          key: `${i}`,
          value: `Tisch ${i}`,
        })
      }
    }
    return tables
  }

  const playerList = () => {
    const newPlayerList: string[] = []
    props.eventMember?.map(elem => {
      elem.gamer && newPlayerList.push(elem.memberId)
    })
    setPlayerArray(newPlayerList)
  }

  const getPlayerOptions = () => {
    const options: DropDownElement[] = []
    members?.results.map(member => {
      if (member.originId && !playerArray.find(elem => elem === member.originId)) {
        options.push({
          key: member.originId,
          value: getPlayerName(member.originId) || member.originId,
        })
      }
    })
    setPlayerOptions(options)
  }
  const getSessionPlayers = () => {
    const elements: DropDownElement[] = []
    props.eventMember?.forEach(member => {
      if (member.gamer && member.id) {
        elements.push({
          key: member.memberId,
          value: `${members?.results.find(elem => elem.originId === member.memberId)?.firstname} ${members?.results.find(elem => elem.originId === member.memberId)?.lastname}`,
        })
      }
    })
    return elements
  }

  useEffect(() => {
    playerList()
  }, [props.eventMember, props])

  useEffect(() => {
    getPlayerOptions()
  }, [playerArray])

  const TableAvatar = (player: Member | undefined) => {
    const avatarSize = downMd ? '50px' : '70px'
    return (
      <>
        {player ? (
          <Avatar
            sx={{
              bgcolor: '#fff',
              color: theme.palette.primary.light,
              width: avatarSize,
              height: avatarSize,
            }}
            alt={`${player.firstname} ${player.lastname}`}
            src={player.profileImageUrl}
          >
            <div style={{
              fontSize: '1rem',
              fontStyle: 'bold',
              textAlign: 'center',
            }}
            >{`${player.firstname}`} <br /> {`${player.lastname.substring(0, 1).toUpperCase()}.`}
            </div>
          </Avatar>
        ) : (
          <Avatar
            sx={{
              color: '#fff',
              bgcolor: theme.palette.primary.light,
              border: '2px solid #fff',
              width: avatarSize,
              height: avatarSize,
            }}
            alt='?'
          >
            <b>
              ?
            </b>
          </Avatar>
        )}
      </>)
  }

  const TableAvatarBox = (eventResult: EventResult) => {
    const player1: Member | undefined = members?.results.find(member => member.originId === eventResult.players[0])
    const player2: Member | undefined = members?.results.find(member => member.originId === eventResult.players[1])
    const player3: Member | undefined = members?.results.find(member => member.originId === eventResult.players[2])
    const player4: Member | undefined = members?.results.find(member => member.originId === eventResult.players[3])

    if (!eventResult.results) {
      eventResult.results = {}
    }
    if (Object.keys(eventResult.results).length === 0) {
      eventResult.results = getDefaultCompleteResult(eventResult.players)
    }
    return (
      <Box
        onClick={() => {
          if (eventStarted || eventResult) {
            setTable(eventResult.table)
            setSession(eventResult.session)
            setPlayer1(eventResult.players[0])
            setPlayer2(eventResult.players[1])
            setPlayer3(eventResult.players[2])
            setPlayer4(eventResult.players[3])
            setSelectedEventResult(eventResult)

            if (event?.tablesClosed) {
              setShowResultDialog(true)
            } else {
              setEditResultDialog(true)
            }
          }
        }}
        sx={{
          display: 'flex',
          width: downMd ? '150px' : '175px',
          height: downMd ? '150px' : '175px',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          bgcolor: theme.palette.primary.light,
          borderRadius: '10px',
          padding: '10px',
          position: 'relative',
          cursor: 'pointer',
          marginBottom: '20px',
          marginRight: '20px',
        }}
      >
        {TableAvatar(player1)}
        {TableAvatar(player2)}
        {TableAvatar(player3)}
        {eventResult.players[3] && TableAvatar(player4)}
        <Typography
          variant='h1'
          sx={{
            opacity: 0.6,
            position: 'absolute',
            color: '#fff',
            top: downMd ? '40px' : '54px',
            left: downMd ? '64px' : '77px',
          }}
        ><b>{eventResult.table}</b>
        </Typography>
      </Box>
    )
  }
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
  const sendToBackend = async (game: any): Promise<void> => {
    const restUrl = `${restPath}/api/${getApiVersion()}/game`
    try {
      fetch(restUrl,
        {
          method: 'POST',
          body: JSON.stringify(
            {
              game: game,
            }),
          credentials: 'include',
          headers: requestHeaders,
        })
        .then(res => res.json())
        .then((json) => {
          console.log(json)
        })
        .catch(err => console.log(err))
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <>
      {(user?.userRole === UserRole.admin || user?.userRole === UserRole.jury) &&
        <>
          <Typography variant='body1'>Nachfolgend werden die Ergebnisse des disjährigen Skat-Turniers
            ausgegeben. Als
            Admin / Jury kannst du die Tische zu einem bestimmten Zeitpunkt sperren, dann können keine
            Änderungen mehr
            vorgenommen werden. Auch das Anzeigen der Gesamtergebnisse erfolgt erst nach Freigabe. Beide
            Aktionen können
            jederzeit rückgängig gemacht werden.
          </Typography>

          {/* <ButtonComponent
                       color='primary'
                        variant='text'
                        text='Ergebnisse nachtragen'
                        onClick={async () => {
                            eventResults.map((round, index) => {

                                const restUrl = `${restPath}/api/${getApiVersion()}/round`
                                // neue Runde in DB anlegen
                                fetch(restUrl,
                                    {
                                        method: 'POST',
                                        body: JSON.stringify(
                                            {
                                                round: {
                                                    eventId: props.eventId,
                                                    table: round.table,
                                                    session: round.session,
                                                    players: round.players,
                                                },
                                            }),
                                        credentials: 'include',
                                        headers: requestHeaders,
                                    })
                                    .then(res => res.json())
                                    .then(async (json) => {
                                        const storedEventResult: EventResult | null = json.results.find((elem: EventResult) => elem.table === round.table && elem.session === round.session)

                                        for (const gameNumber of Object.keys(round?.games)) {
                                            const game: any = round?.games[gameNumber]

                                            const sendELem = {
                                                roundId: storedEventResult?.id,
                                                eventId: props.eventId,
                                                gameNumber: parseInt(gameNumber, 10),
                                                gameValue: game.win ? game.gameValue : (game.gameValue * -2),
                                                gameProperty: game.gameProperty,
                                                mitOhne: game.mitOhne,
                                                hand: game.hand,
                                                game: game.game || 'custom',
                                                won: game.win,
                                                factor: game.factor,
                                                player: game.player,
                                            }
                                            storedEventResult?.id && game && sendToBackend(sendELem)
                                            await delay(10000)
                                        }
                                    })
                            })
                        }}
                    /> */}
          {!event?.tablesClosed &&
            <ButtonComponent
              color='primary'
              variant='text'
              text='Tische schließen'
              onClick={() => {
                const actualEvent = event
                if (actualEvent) {
                  actualEvent.tablesClosed = true
                  fetchData(basePath, getApiVersion(), fetchHeaders, Entity.events, Method.PUT, props.eventId, { event: actualEvent }, undefined, setMessage)
                }
              }}
            />}
          {event?.tablesClosed &&
            <ButtonComponent
              color='primary'
              variant='text'
              text='Bearbeitung freigeben'
              onClick={() => {
                const actualEvent = event
                if (actualEvent) {
                  actualEvent.tablesClosed = false
                  setEvent(actualEvent)
                  fetchData(basePath, getApiVersion(), fetchHeaders, Entity.events, Method.PUT, props.eventId, { event: actualEvent }, undefined, setMessage)
                }
              }}
            />}
          {!event?.displayResults &&
            <ButtonComponent
              color='primary'
              variant='text'
              text='Ergebnisse anzeigen'
              onClick={() => {
                const actualEvent = event
                if (actualEvent) {
                  actualEvent.displayResults = true
                  fetchData(basePath, getApiVersion(), fetchHeaders, Entity.events, Method.PUT, props.eventId, { event: actualEvent }, undefined, setMessage)
                }
              }}
            />}
          {event?.displayResults &&
            <ButtonComponent
              color='primary'
              variant='text'
              text='Ergebnisse verbergen'
              onClick={() => {
                const actualEvent = event
                if (actualEvent) {
                  actualEvent.displayResults = false
                  fetchData(basePath, getApiVersion(), fetchHeaders, Entity.events, Method.PUT, props.eventId, { event: actualEvent }, undefined, setMessage)
                }
              }}
            />}
        </>}

      <Dialog
        open={showResultDialog}
        onClose={() => setShowResultDialog(false)}
        sx={{ '& .MuiDialog-paper': { borderRadius: 0, maxWidth: '90%', minWidth: '90%' } }}
      >
        <Stack sx={{ p: 3 }}>
          <Stack
            direction='row' spacing={3} alignItems='center'
            justifyContent='space-between'
            className={classes.baseBox}
          >
            <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px', marginBottom: '20px' }}>
              {`Ergebnisse ${session}. Runde: Tisch ${table}`}
            </DialogTitle>
          </Stack>
          <DialogContent sx={{ p: 0, overflow: 'visible' }}>
            <>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={classes.tableHeader}
                      sx={{ width: '80px', maxWidth: '80px' }}
                    >
                      Runde
                    </TableCell>
                    <TableCell
                      className={classes.tableHeader}
                      sx={{ width: '100px', maxWidth: '100px' }}
                    >
                      Spielwert
                    </TableCell>
                    <TableCell className={classes.tableHeader} sx={{ minWidth: '120px' }}>
                      {player1 && getPlayerName(player1)}
                    </TableCell>
                    <TableCell className={classes.tableHeader} sx={{ minWidth: '120px' }}>
                      {player2 && getPlayerName(player2)}
                    </TableCell>
                    <TableCell className={classes.tableHeader} sx={{ minWidth: '120px' }}>
                      {player3 && getPlayerName(player3)}
                    </TableCell>
                    {player4 &&
                      <TableCell className={classes.tableHeader} sx={{ minWidth: '120px' }}>
                        {player4 && getPlayerName(player4)}
                      </TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rounds.map((round) => {
                    return (
                      <TableRow key={round}>
                        <TableCell align='center'>
                          {round}
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body1'>
                            {selectedEventResult?.games[round]?.gameValue || 0}
                          </Typography>
                        </TableCell>
                        <TableCell
                          className={(round % (player4 ? 4 : 3)) === 1 ? classes.dealer : ''}
                        >
                          <Grid container alignItems='center'>
                            <Grid
                              item xs={6}
                              sx={{ width: '50px', maxWidth: '50px' }}
                            >
                              <Typography className={classes.playerResult}>
                                {selectedEventResult?.games[round]?.game === Game.eingemischt ? ((round % (player4 ? 4 : 3)) === 1 ? (player1 && selectedEventResult?.games[round]?.results[player1] ? selectedEventResult?.games[round]?.results[player1] : 0) : '-') : (player1 && selectedEventResult?.games[round]?.player === player1 && selectedEventResult?.games[round]?.gameValue ? selectedEventResult?.games[round]?.results[player1] : '-')}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex' }}>
                              <CheckBoxComponent
                                label='gew.'
                                value={player1 && selectedEventResult?.games[round]?.player === player1 ? selectedEventResult?.games[round]?.won : false}
                                disabled
                              />
                              <CheckBoxComponent
                                label='verl.'
                                value={player1 && selectedEventResult?.games[round]?.player === player1 ? !selectedEventResult?.games[round]?.won : false}
                                disabled
                              />
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell
                          className={(round % (player4 ? 4 : 3)) === 2 ? classes.dealer : ''}
                        >
                          <Grid container alignItems='center'>
                            <Grid
                              item xs={6}
                              sx={{ width: '50px', maxWidth: '50px' }}
                            >
                              <Typography className={classes.playerResult}>
                                {selectedEventResult?.games[round]?.game === Game.eingemischt ? ((round % (player4 ? 4 : 3)) === 2 ? (player2 && selectedEventResult?.games[round]?.results[player2] ? selectedEventResult?.games[round]?.results[player2] : 0) : '-') : (player2 && selectedEventResult?.games[round]?.player === player2 && selectedEventResult?.games[round]?.gameValue ? selectedEventResult?.games[round]?.results[player2] : '-')}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex' }}>
                              <CheckBoxComponent
                                disabled
                                label='gew.'
                                value={player2 && selectedEventResult?.games[round]?.player === player2 ? selectedEventResult?.games[round]?.won : false}
                              />
                              <CheckBoxComponent
                                disabled
                                label='verl.'
                                value={player2 && selectedEventResult?.games[round]?.player === player2 ? !selectedEventResult?.games[round]?.won : false}
                              />
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell
                          className={(round % (player4 ? 4 : 3)) === (player4 ? 3 : 0) ? classes.dealer : ''}
                        >
                          <Grid container alignItems='center'>
                            <Grid
                              item xs={6}
                              sx={{ width: '50px', maxWidth: '50px' }}
                            >
                              <Typography className={classes.playerResult}>
                                {selectedEventResult?.games[round]?.game === Game.eingemischt ? ((round % (player4 ? 4 : 3)) === 0 ? (player3 && selectedEventResult?.games[round]?.results[player3] ? selectedEventResult?.games[round]?.results[player3] : 0) : '-') : (player3 && selectedEventResult?.games[round]?.player === player3 && selectedEventResult?.games[round]?.gameValue ? selectedEventResult?.games[round]?.results[player3] : '-')}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex' }}>
                              <CheckBoxComponent
                                label='gew.'
                                value={player3 && selectedEventResult?.games[round]?.player === player3 ? selectedEventResult?.games[round]?.won : false}
                                disabled
                              />
                              <CheckBoxComponent
                                label='verl.'
                                value={player3 && selectedEventResult?.games[round]?.player === player3 ? !selectedEventResult?.games[round]?.won : false}
                                disabled
                              />
                            </Grid>
                          </Grid>
                        </TableCell>
                        {player4 &&
                          <TableCell
                            className={(round % (player4 ? 4 : 3)) === 0 ? classes.dealer : ''}
                          >
                            <Grid container alignItems='center'>
                              <Grid
                                item xs={6}
                                sx={{ width: '50px', maxWidth: '50px' }}
                              >
                                <Typography className={classes.playerResult}>
                                  {selectedEventResult?.games[round]?.game === Game.eingemischt ? ((round % (player4 ? 4 : 3)) === 0 ? (player4 && selectedEventResult?.games[round]?.results[player4] ? selectedEventResult?.games[round]?.results[player4] : 0) : '-') : (player4 && selectedEventResult?.games[round]?.player === player4 && selectedEventResult?.games[round]?.gameValue ? selectedEventResult?.games[round]?.results[player4] : '-')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sx={{ display: 'flex' }}>
                                <CheckBoxComponent
                                  label='gew.'
                                  value={player4 && selectedEventResult?.games[round]?.player === player4 ? selectedEventResult?.games[round]?.won : false}
                                  disabled
                                />
                                <CheckBoxComponent
                                  label='verl.'
                                  value={player4 && selectedEventResult?.games[round]?.player === player4 ? !selectedEventResult?.games[round]?.won : false}
                                  disabled
                                />
                              </Grid>
                            </Grid>
                          </TableCell>}
                      </TableRow>)
                  },
                  )}
                </TableBody>
                <TableHead className={classes.stickyFooter}>
                  <TableRow>
                    <TableCell
                      className={classes.footerTitle}
                      colSpan={2}
                    >Ergebnis
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player1 && selectedEventResult?.results[player1]?.sessionResult}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player2 && selectedEventResult?.results[player2]?.sessionResult}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player3 && selectedEventResult?.results[player3]?.sessionResult}
                    </TableCell>
                    {player4 &&
                      <TableCell
                        className={classes.footer}
                      >{player4 && selectedEventResult?.results[player4]?.sessionResult}
                      </TableCell>}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      className={classes.footerTitle}
                      colSpan={2}
                    >Gew. / Verl.
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{`${player1 && selectedEventResult?.results[player1]?.wonOffense} / ${player1 && selectedEventResult?.results[player1]?.lostOffense}`}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{`${player2 && selectedEventResult?.results[player2]?.wonOffense} / ${player2 && selectedEventResult?.results[player2]?.lostOffense}`}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{`${player3 && selectedEventResult?.results[player3]?.wonOffense} / ${player3 && selectedEventResult?.results[player3]?.lostOffense}`}
                    </TableCell>
                    {player4 &&
                      <TableCell
                        className={classes.footer}
                      >{`${player4 && selectedEventResult?.results[player4]?.wonOffense} / ${player4 && selectedEventResult?.results[player4]?.lostOffense}`}
                      </TableCell>}
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.footerTitle} colSpan={2}>
                      +50 je gew. Spiel <br />
                      -50 je verl. Spiel
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player1 && ((selectedEventResult?.results[player1]?.wonOffense || 0) - (selectedEventResult?.results[player1]?.lostOffense || 0)) * 50}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player2 && ((selectedEventResult?.results[player2]?.wonOffense || 0) - (selectedEventResult?.results[player2]?.lostOffense || 0)) * 50}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player3 && ((selectedEventResult?.results[player3]?.wonOffense || 0) - (selectedEventResult?.results[player3]?.lostOffense || 0)) * 50}
                    </TableCell>
                    {player4 &&
                      <TableCell
                        className={classes.footer}
                      >{player4 && ((selectedEventResult?.results[player4]?.wonOffense || 0) - (selectedEventResult?.results[player4]?.lostOffense || 0)) * 50}
                      </TableCell>}
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.footerTitle} colSpan={2}>
                      +{player4 ? 30 : 40} je verlorenes Spiel des Gegners
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player1 && (selectedEventResult?.results[player1]?.lostDefense || 0) * (player4 ? 30 : 40)}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player2 && (selectedEventResult?.results[player2]?.lostDefense || 0) * (player4 ? 30 : 40)}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player3 && (selectedEventResult?.results[player3]?.lostDefense || 0) * (player4 ? 30 : 40)}
                    </TableCell>
                    {player4 &&
                      <TableCell
                        className={classes.footer}
                      >{player4 && (selectedEventResult?.results[player4]?.lostDefense || 0) * 30}
                      </TableCell>}
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.footerTitle} colSpan={2}>Gesamtergebnis
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player1 && selectedEventResult?.results[player1]?.completeResult}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player2 && selectedEventResult?.results[player2]?.completeResult}
                    </TableCell>
                    <TableCell
                      className={classes.footer}
                    >{player3 && selectedEventResult?.results[player3]?.completeResult}
                    </TableCell>
                    {player4 &&
                      <TableCell
                        className={classes.footer}
                      >{player4 && selectedEventResult?.results[player4]?.completeResult}
                      </TableCell>}
                  </TableRow>
                </TableHead>
              </Table>
            </>
          </DialogContent>
        </Stack>
      </Dialog>

      <Dialog
        open={editResultDialog && event?.tablesClosed === false}
        onClose={() => setEditResultDialog(false)}
        sx={{ '& .MuiDialog-paper': { borderRadius: 0, maxWidth: '95%', minWidth: '95%' } }}
      >
        <Stack sx={{ p: downMd ? 1 : 3 }}>
          <Stack
            direction='row' spacing={downMd ? 1 : 3} alignItems='center'
            justifyContent='space-between'
            className={classes.baseBox}
          >
            <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px', marginBottom: '20px' }}>
              Ergebnisse eintragen
            </DialogTitle>
          </Stack>
          <DialogContent sx={{ p: 0, overflow: 'visible' }}>
            <NewEventResults
              eventResult={selectedEventResult} event={event}
              saveAllowed={(value) => setSaveAllowed(value)}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <ButtonComponent
              color='secondary'
              variant='text'
              text='Abbrechen'
              onClick={() => setEditResultDialog(false)}
            />
            <ButtonComponent
              color='primary'
              variant='contained'
              text='Speichern'
              disabled={!saveAllowed}
              onClick={() => {
                setEditResultDialog(false)
              }}
            />
          </DialogActions>
        </Stack>
      </Dialog>
      {eventResults.length > 0
        ? (
          <Stack
            direction='column' spacing={2} alignItems='flex-start'
            justifyContent='space-between'
          >
            {(event?.displayResults || user?.userRole === UserRole.jury || user?.userRole === UserRole.admin) &&
              <Stack sx={{ width: '100%', marginRight: '20px' }}>
                <Table size='small' sx={{ width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>
                        <b>Nr.</b>
                      </TableCell>
                      <TableCell>
                        <b>Name</b>
                      </TableCell>
                      {!downMd &&
                        <>
                          <TableCell
                            align='center' sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                            onClick={() => setSortingOption(SortingOption.round1)}
                          >
                            <b>Runde 1</b>
                          </TableCell>
                          <TableCell
                            align='center' sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                            onClick={() => setSortingOption(SortingOption.round2)}
                          >
                            <b>Runde 2</b>
                          </TableCell>
                        </>}
                      <TableCell
                        align='center' sx={{ cursor: 'pointer' }}
                        onClick={() => setSortingOption(SortingOption.complete)}
                      >
                        <b>Gesamt</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eventResults && getSortedResults(eventResults, sortingOption).map((element, index) =>
                      <TableRow key={index}>
                        <TableCell align='center'>
                          <b>{index + 1}</b>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {getPlayerName(element.memberId)}
                        </TableCell>
                        {!downMd &&
                          <>
                            <TableCell align='center'>
                              {eventResults.find(result => result.session === 1 && result.players.find(player => player === element.memberId))?.results[element.memberId].completeResult}
                            </TableCell>
                            <TableCell align='center'>
                              {eventResults.find(result => result.session === 2 && result.players.find(player => player === element.memberId))?.results[element.memberId].completeResult}
                            </TableCell>
                          </>}
                        <TableCell align='center'>
                          <b>{element.completeResult}</b>
                        </TableCell>
                      </TableRow>,
                    )}
                  </TableBody>
                </Table>
              </Stack>}
            <Stack
              direction='column' spacing={2} alignItems='flex-start'
              justifyContent='space-between'
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant='body1'><b>Runde 1</b></Typography>
                </Box>
                {(event?.displayResults || user?.userRole === UserRole.admin || user?.userRole === UserRole.jury)
                  ? (
                    <>
                      {eventResults.sort((a, b) => a.table - b.table).map((result) => {
                        return (
                          <>
                            {result.session === 1 && (
                              <>
                                {TableAvatarBox(result)}
                              </>)}
                          </>
                        )
                      },
                      )}
                    </>)
                  : (
                    <>
                      {user?.memberId && eventResults.filter(result => result.players.includes(user?.memberId)).sort((a, b) => a.table - b.table).map((result) => {
                        return (
                          <>
                            {result.session === 1 && (
                              <>
                                {TableAvatarBox(result)}
                              </>)}
                          </>
                        )
                      },
                      )}
                    </>
                  )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant='body1'><b>Runde 2</b></Typography>
                </Box>
                {(event?.displayResults || user?.userRole === UserRole.admin || user?.userRole === UserRole.jury)
                  ? (
                    <>
                      {eventResults.sort((a, b) => a.table - b.table).map((result) => {
                        return (
                          <>
                            {result.session === 2 && (
                              <>
                                {TableAvatarBox(result)}
                              </>)}
                          </>
                        )
                      },
                      )}
                    </>)
                  : (
                    <>
                      {user?.memberId && eventResults.filter(result => result.players.includes(user?.memberId)).sort((a, b) => a.table - b.table).map((result) => {
                        return (
                          <>
                            {result.session === 2 && (
                              <>
                                {TableAvatarBox(result)}
                              </>)}
                          </>
                        )
                      },
                      )}
                    </>
                  )}
              </Box>
            </Stack>
          </Stack>)
        : (
          <>
            <Typography variant='body1'>Noch keine Ergebnisse.</Typography>
            {(user?.userRole === UserRole.admin) &&
              <ButtonComponent
                onClick={() => {
                  window.localStorage.setItem('openNewStartDialog', 'true')
                  setOpenNewStartDialog(true)
                  setEventStartet(true)
                }}
                text='Neues Skatturnier starten'
              />}
          </>)}
      <Dialog
        open={openNewStartDialog}
        onClose={() => {
          setOpenNewStartDialog(false)
          window.localStorage.setItem('openNewStartDialog', 'false')
          window.localStorage.removeItem('activeEventTab')
        }}
        sx={{ '& .MuiDialog-paper': { borderRadius: 0, maxWidth: '95%', minWidth: '95%' } }}
      >
        <Stack sx={{ p: downMd ? 1 : 3 }}>
          <Stack
            direction='row' spacing={downMd ? 1 : 3} alignItems='center'
            justifyContent='space-between'
            className={classes.baseBox}
          >
            <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px', marginBottom: '20px' }}>
              Neues Skatturnier starten
            </DialogTitle>
          </Stack>
          <DialogContent sx={{ p: 0, overflow: 'visible' }}>
            <>
              <Typography variant='body1'>
                Willkommen beim diesjährigen Skatturnier. Bevor wir die Tische verteilen und das Turnier
                starten, hast du jetzt die Gelegenheit, die Spieler festzulegen. <br />
                Wir wünschen allen Teilnehmern viel Erfolg und vor allem viel Spaß beim Spiel!
              </Typography>
              <Grid container>
                <Grid item xs={6}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeader} sx={{ width: '20px' }}>
                          &nbsp;
                        </TableCell>
                        <TableCell className={classes.tableHeader}>
                          Vorname
                        </TableCell>
                        <TableCell className={classes.tableHeader}>
                          Nachname
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {props.eventMember?.map((member: EventMember) => {
                        return (
                          <>
                            {member.gamer &&
                              <TableRow key={member.id}>
                                <TableCell>
                                  <CheckBoxComponent
                                    value={member.gamer} onChange={(checked) => {
                                      member.gamer = false
                                      fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventMembers, Method.PUT, member.id, { eventMember: member }, setEventMember, setMessage)
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {members?.results.find(elem => elem.originId === member.memberId)?.firstname}
                                </TableCell>
                                <TableCell>
                                  {members?.results.find(elem => elem.originId === member.memberId)?.lastname}
                                </TableCell>
                              </TableRow>}
                          </>
                        )
                      }
                      )}
                    </TableBody>
                  </Table>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <DropDownComponent
                      options={playerOptions}
                      value={newGamerId}
                      label='Weiteren Spieler hinzufügen'
                      onChange={(value) => {
                        value?.key && setNewGamerId(value.key)
                      }}
                    />
                    <IconButton onClick={() => {
                      const existingEventMember = props.eventMember?.find(elem => elem.memberId === newGamerId)
                      if (newGamerId) {
                        if (existingEventMember) {
                          existingEventMember.gamer = true
                          fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventMembers, Method.PUT, existingEventMember.id, { eventMember: existingEventMember }, setEventMember, setMessage)
                        } else {
                          const newEventMember: EventMember = {
                            registeredByMemberId: newGamerId,
                            eventId: props.eventId,
                            memberId: newGamerId,
                            participate: true,
                            gamer: true,
                            from: {},
                            to: {},
                            eventPriceCategories: [],
                          }
                          fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventMembers, Method.POST, undefined, { eventMember: newEventMember }, setEventMember, setMessage)
                        }
                        setNewGamerId(undefined)
                      }
                    }}
                    ><SaveIcon />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ marginBottom: '20px' }}>
                    <ButtonComponent
                      fullWidth
                      text={`${playerArray.length} Spieler an Tische zufällig verteilen`}
                      onClick={() => {
                        setShowTablePlayerDialog(false)
                        const getPlayerTables = () => {
                          const playerTables: PlayerTables = {}
                          const shuffle = () => {
                            return Math.random() - 0.5
                          }
                          const shuffledArray = playerArray.sort(shuffle)
                          // prüfen ob Handicap-Spieler mitspielen
                          let handicapPlayer = false
                          shuffledArray.map((player) => {
                            const member = members?.results.find(member => member.originId === player)
                            if (member?.handicap) {
                              handicapPlayer = true
                            }
                          })
                          let playerCount = 3
                          if (handicapPlayer) {
                            playerCount = 3
                          } else {
                            // mod 3 oder mod 4 === 0 > die Zahl jeweils nehmen
                            if (playerCount % 4 === 0) {
                              playerCount = 4
                            }
                          }

                          const tableCount = Math.floor(shuffledArray.length / playerCount)
                          for (let table = 1; table <= tableCount; table += 1) {
                            playerTables[table] = []
                          }
                          let i = 0
                          shuffledArray.map((elem) => {
                            i += 1
                            const table = (i % tableCount) + 1
                            playerTables[table].push(elem)
                          })
                          return playerTables
                        }
                        const validTables = (playerTables: PlayerTables) => {
                          // prüfen ob genauso viele 3er Tische, wie Handicap-Spieler da sind, sonst return true
                          let tableWithThreePlayers = 0
                          let handicapPlayers = 0
                          Object.keys(playerTables).map((table: any) => {
                            if (playerTables[table].length === 3) {
                              tableWithThreePlayers += 1
                            }

                            playerTables[table].map((player: string) => {
                              const member = members?.results.find(member => member.originId === player)
                              if (member?.handicap) {
                                handicapPlayers += 1
                              }
                            })
                          })
                          if (handicapPlayers > tableWithThreePlayers) {
                            return true
                          } else {
                            // prüfen, ob alle mit handycap an verschiedenen Tischen sitzen
                            // und nicht an 4er Tischen
                            let valid = true
                            Object.keys(playerTables).map((table: any) => {
                              let handicapCount = 0
                              playerTables[table].map((player: string) => {
                                const member = members?.results.find(member => member.originId === player)
                                if (member?.handicap) {
                                  handicapCount += 1
                                }
                              })
                              if (handicapCount > 1 || (handicapCount === 1 && playerTables[table].length === 4)) {
                                valid = false
                              }
                            })
                            return valid
                          }
                        }

                        const newEventResult: EventResult [] = []
                        for (let i = 1; i < 3; i += 1) {
                          let valid = false
                          let possibleTables: PlayerTables = {}
                          while (!valid) {
                            possibleTables = getPlayerTables()
                            if (validTables(possibleTables)) {
                              valid = true
                            }
                          }
                          Object.keys(possibleTables).map((table: any) => {
                            const results = {}
                            possibleTables[table].map((player: string) => {
                              results[player] = {
                                sessionResult: 0,
                                wonOffense: 0,
                                lostOffense: 0,
                                lostDefense: 0,
                                offensePoints: 0,
                                defensePoints: 0,
                                completeResult: 0,
                              }
                            })
                            const newResult: EventResult = {
                              eventId: props.eventId,
                              table,
                              session: i,
                              players: possibleTables[table],
                              games: {},
                              results,
                            }
                            newEventResult.push(newResult)
                          })
                        }

                        setEventResults(newEventResult)
                        setStartAllowed(true)
                      }}
                    />
                  </Box>
                  <Box>
                    {user?.userRole === UserRole.admin &&
                      <ButtonComponent
                        fullWidth
                        text={`${playerArray.length} Spieler an Tische direkt verteilen`}
                        onClick={() => {
                          setShowTablePlayerDialog(true)
                        }}
                      />}
                  </Box>
                  {showTablePlayerDialog &&
                    <>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.tableHeader}>
                              Tische
                            </TableCell>
                            <TableCell className={classes.tableHeader}>
                              Runde 1
                            </TableCell>
                            <TableCell className={classes.tableHeader}>
                              Runde 2
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getSessionTables().map((table: DropDownElement) => {
                            return (
                              <>
                                <TableRow key={table.key}>
                                  <TableCell>
                                    {table.value}
                                  </TableCell>
                                  <TableCell>
                                    <DropDownComponent
                                      multiple
                                      size='small'
                                      margin='none'
                                      label='Spieler auswählen'
                                      options={getSessionPlayers()}
                                      onChangeList={(multipleValues) => {
                                        const players: string[] = []
                                        multipleValues.forEach(elem => {
                                          players.push(elem.key)
                                        })

                                        const results: EventResult = {}
                                        players.map((player: string) => {
                                          results[player] = {
                                            sessionResult: 0,
                                            wonOffense: 0,
                                            lostOffense: 0,
                                            lostDefense: 0,
                                            offensePoints: 0,
                                            defensePoints: 0,
                                            completeResult: 0,
                                          }
                                        })
                                        const newList: EventResult [] = eventResults
                                        let elementFound = false
                                        newList.map(resultElement => {
                                          if (resultElement.table === parseInt(table.key, 10) && resultElement.session === 1) {
                                            elementFound = true
                                            resultElement.players = players
                                            resultElement.results = results
                                          }
                                          return resultElement
                                        })
                                        if (!elementFound) {
                                          newList.push(
                                            {
                                              eventId: props.eventId,
                                              table: parseInt(table.key, 10),
                                              session: 1,
                                              players: players,
                                              games: {},
                                              results: results,
                                            },
                                          )
                                        } else {
                                          setStartAllowed(true)
                                        }
                                        setEventResults(newList)
                                        console.log(newList)
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <DropDownComponent
                                      multiple
                                      size='small'
                                      margin='none'
                                      label='Spieler auswählen'
                                      options={getSessionPlayers()}
                                      onChangeList={(multipleValues) => {
                                        const players: string[] = []
                                        multipleValues.forEach(elem => {
                                          players.push(elem.key)
                                        })

                                        const results: EventResult = {}
                                        players.map((player: string) => {
                                          results[player] = {
                                            sessionResult: 0,
                                            wonOffense: 0,
                                            lostOffense: 0,
                                            lostDefense: 0,
                                            offensePoints: 0,
                                            defensePoints: 0,
                                            completeResult: 0,
                                          }
                                        })
                                        const newList: EventResult [] = eventResults
                                        let elementFound = false
                                        newList.map(resultElement => {
                                          if (resultElement.table === parseInt(table.key, 10) && resultElement.session === 2) {
                                            elementFound = true
                                            resultElement.players = players
                                            resultElement.results = results
                                          }
                                          return resultElement
                                        })
                                        if (!elementFound) {
                                          newList.push(
                                            {
                                              eventId: props.eventId,
                                              table: parseInt(table.key, 10),
                                              session: 2,
                                              players: players,
                                              games: {},
                                              results: results,
                                            },
                                          )
                                        } else {
                                          setStartAllowed(true)
                                        }
                                        setEventResults(newList)
                                        console.log(newList)
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              </>
                            )
                          },
                          )}
                        </TableBody>
                      </Table>
                    </>}
                  {eventResults.length > 0 && !showTablePlayerDialog &&
                    <Stack
                      direction='column' spacing={2} alignItems='flex-start'
                      justifyContent='space-between'
                    >
                      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant='body1'><b>Runde 1</b></Typography>
                        </Box>
                        {eventResults.sort((a, b) => a.table - b.table).map((result) => {
                          return (
                            <>
                              {result.session === 1 && (
                                <>
                                  {TableAvatarBox(result)}
                                </>)}
                            </>
                          )
                        },
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant='body1'><b>Runde 2</b></Typography>
                        </Box>
                        {eventResults.sort((a, b) => a.table - b.table).map((result) => {
                          return (
                            <>
                              {result.session === 2 && (
                                <>
                                  {TableAvatarBox(result)}
                                </>)}
                            </>
                          )
                        },
                        )}
                      </Box>
                    </Stack>}
                </Grid>
              </Grid>
            </>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <ButtonComponent
              color='secondary'
              variant='text'
              text='Abbrechen'
              onClick={() => {
                window.localStorage.setItem('openNewStartDialog', 'false')
                window.localStorage.removeItem('activeEventTab')
                setOpenNewStartDialog(false)
              }}
            />
            <ButtonComponent
              color='primary'
              variant='contained'
              text='Turnier starten'
              disabled={!startAllowed}
              onClick={() => {
                setEventStartet(true)
                setOpenNewStartDialog(false)
                window.localStorage.setItem('openNewStartDialog', 'false')
                window.localStorage.removeItem('activeEventTab')
                eventResults.map(round => {
                  // neue Runden in DB anlegen
                  const restUrl = `${restPath}/api/${getApiVersion()}/round`
                  fetch(restUrl,
                    {
                      method: 'POST',
                      body: JSON.stringify(
                        {
                          round: {
                            eventId: props.eventId,
                            table: round.table,
                            session: round.session,
                            players: round.players,
                          },
                        }),
                      credentials: 'include',
                      headers: requestHeaders,
                    })
                    .then(res => res.json())
                    .then((json) => {
                      setEventResults(json)
                    })
                    .catch(err => console.log(err))
                })
              }}
            />
          </DialogActions>
        </Stack>
      </Dialog>
    </>
  )
}

export default EventResults
